import { PrivacyBlur } from "#components/privacy-blur"
import { useTheme } from "#components/theme-provider"
import { Card, CardContent, CardTitle } from "#components/ui/card"
import { StatLabel, StatValue } from "#components/ui/typography"
import { useCurrencyFormatters } from "#hooks/use-currency-formatters"
import { useI18n } from "#hooks/use-i18n"
import { cardEntrance, getCardHoverEffect, statValue } from "#lib/animations"
import { formatDate } from "#lib/formatters"
import type { DashboardAnalyticsResponse } from "@home-finance-manager/shared/types/dashboard"
import { motion } from "framer-motion"
import React from "react"

interface TopExpensesCardProps {
  analytics: DashboardAnalyticsResponse | undefined
}

export const TopExpensesCard = React.memo<TopExpensesCardProps>(({ analytics }) => {
  const { t } = useI18n()
  const { formatCurrency } = useCurrencyFormatters()
  const { resolvedTheme } = useTheme()
  const isDark = resolvedTheme === "dark"

  // Get the most recent month's data (last in the array, as they're sorted chronologically)
  const selectedMonthIndex = React.useMemo(() => {
    if (!analytics?.topExpensesByMonth || analytics.topExpensesByMonth.length === 0) return -1
    return analytics.topExpensesByMonth.length - 1
  }, [analytics])

  const selectedMonthData =
    selectedMonthIndex >= 0 ? analytics?.topExpensesByMonth?.[selectedMonthIndex] || null : null
  const topExpenses = selectedMonthData?.topExpenses || []

  // Calculate total of top expenses
  const totalTopExpenses = topExpenses.reduce((sum, expense) => sum + expense.amount, 0)

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={cardEntrance}
      whileHover={getCardHoverEffect(isDark)}
      className="h-full min-h-0 flex flex-col"
    >
      <Card className="flex flex-col overflow-hidden h-full max-h-full border-border/50 bg-card/50 backdrop-blur-sm shadow-sm dark:shadow-none dark:border-border/50 transition-shadow hover:shadow-md dark:hover:shadow-none">
        {/* Accent border */}
        <div
          className={`h-1 absolute top-0 left-0 right-0 ${
            isDark
              ? "bg-gradient-to-r from-primary/60 via-primary/40 to-transparent"
              : "bg-gradient-to-r from-primary/80 via-primary/60 to-transparent"
          }`}
        />
        <CardContent className="p-6 flex-1 flex flex-col min-h-0 overflow-hidden">
          <div className="mb-4 flex-shrink-0">
            <CardTitle className="text-base font-medium mb-3 text-foreground">
              {t("components.dashboard.topExpenses.title")}
            </CardTitle>
            <motion.div variants={statValue}>
              <StatValue className="text-3xl sm:text-4xl mb-2">
                {formatCurrency(totalTopExpenses)}
              </StatValue>
            </motion.div>
            <StatLabel>
              {t("components.dashboard.topExpenses.subtitle", { count: topExpenses.length })}
            </StatLabel>
          </div>

          {/* Expenses List */}
          <div className="flex-1 min-h-0 overflow-y-auto space-y-0 pr-1">
            {topExpenses.length > 0 ? (
              topExpenses.map((expense, index) => (
                <div
                  key={expense.expenseId}
                  className="relative flex flex-col gap-1.5 group hover:bg-muted/30 rounded-md px-3 py-2 transition-colors border border-transparent hover:border-border/50"
                >
                  {index < topExpenses.length - 1 && (
                    <div
                      className="absolute bottom-0 left-0 right-0 h-px pointer-events-none"
                      style={{
                        background: `linear-gradient(to right, transparent 0%, var(--border) 20%, var(--border) 80%, transparent 100%)`,
                        opacity: 0.5,
                      }}
                    />
                  )}
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-2 min-w-0 flex-1">
                      <span className="text-xs text-muted-foreground font-medium flex-shrink-0 mt-0.5">
                        #{index + 1}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <div
                            className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                            style={{
                              backgroundColor: expense.categoryColor,
                              boxShadow: `0 0 8px ${expense.categoryColor}40`,
                            }}
                          />
                          <span className="text-foreground font-semibold text-sm truncate">
                            {expense.name}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span className="truncate">{expense.categoryName}</span>
                          {expense.partnerNames.length > 0 && (
                            <>
                              <span>•</span>
                              <span className="truncate">{expense.partnerNames.join(", ")}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <span className="font-bold text-foreground text-sm">
                        <PrivacyBlur>{formatCurrency(expense.amount)}</PrivacyBlur>
                      </span>
                      {expense.nextPaymentDate && (
                        <span className="text-xs text-muted-foreground">
                          {formatDate(expense.nextPaymentDate)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-sm text-muted-foreground text-center">
                  {t("components.dashboard.topExpenses.noExpenses")}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  )
})

TopExpensesCard.displayName = "TopExpensesCard"
